import { apiFetch } from "./client";
import type { Attachment } from "./types";

/**
 * Client360 — Этап 3C «Meeting Surface» API.
 *
 * A THIN surface over the LIVE Phase 3.1A Calendar Meeting Control. Manual,
 * Google-FREE: plan a meeting, list meetings, submit a structured report whose
 * action-items are routed through the 3A Action Engine (report → task/follow-up).
 * Mounts on the same /Custom/frontend/client360/* contract as 3A.
 */

/** Front-facing meeting kind. `visit` is gated (Этап 5) and never sent. */
export type MeetingKind = "meeting" | "online";

export interface ScheduleMeetingPayload {
  kind: MeetingKind;
  plannedAt: string;        // "YYYY-MM-DDTHH:mm" (local datetime-local)
  durationMinutes: 30 | 60 | 90;
  contactId?: string;
  goal?: string;
  expectedResult?: string;
  assignedUserId?: string;  // S4 D-068 — responsible (omit → backend D3=A default)
}

export interface ScheduleMeetingResponse {
  id: string;
  holdingId: string;
  accountId: string;
  kind: MeetingKind;
  plannedAt: string;
  durationMinutes: number;
  goal: string;
  expectedResult: string;
  meetingStatus: string;
  source: "manual";
  assignedUserId?: string;
  responsibleFallback?: boolean;   // D3=A — owner∉team → self-fallback
  responsibleWarning?: string | null;
}

// S4 BE-1 — responsible-picker options + D3=A default.
export interface MeetingResponsibleOptions {
  options: Array<{ id: string; name: string }>;
  defaultId: string;
  responsibleFallback: boolean;
  responsibleWarning: string | null;
  restricted: boolean;             // manager / single-option → locked field
}

export function fetchMeetingResponsibleOptions(
  holdingId: string,
  signal?: AbortSignal,
): Promise<MeetingResponsibleOptions> {
  return apiFetch<MeetingResponsibleOptions>(
    `/Custom/frontend/client360/${encodeURIComponent(holdingId)}/meetings/responsible-options`,
    { signal },
  );
}

// S4 BE-2 — reassign responsible (reason REQUIRED).
export function reassignMeetingResponsible(
  interactionId: string,
  body: { assignedUserId: string; reason: string },
): Promise<{ id: string; assignedUserId: string; changed: boolean }> {
  return apiFetch(
    `/Custom/frontend/client360/meetings/${encodeURIComponent(interactionId)}/responsible`,
    { method: "PATCH", body },
  );
}

/** Report action kind → managed Task (task) / follow-up via 3A engine. */
export type ReportActionKind = "task" | "follow_up";

export interface ReportActionInput {
  kind: ReportActionKind;
  title: string;
  dueDate?: string; // YYYY-MM-DD
}

/** D-055 S2 — C+ result-класифікація: 8 chip-aligned значень live enum `outcome`. */
export type ReportOutcome =
  | "agreed" | "interested" | "followup" | "notready"
  | "contract" | "payment" | "complaint" | "escalate";

/** D-055 S2 — riskMarkers multiEnum (нове поле S1). */
export type ReportRiskMarker = "competitor" | "client_refusal" | "lost_risk";

/**
 * D-055 S3 — staged accepted action (§5): чернетка дії у submit-payload;
 * реальна сутність створюється SERVER-SIDE на submit звіту (одна транзакція,
 * canonical engines). suggestionKey = стабільний ключ картки (ідемпотентність
 * + адресація 422-помилок route_missing тощо).
 */
export interface AcceptedAction {
  kind: "task" | "follow_up" | "internal_request" | "suggestion";
  suggestionKey: string;
  /** D-151 S2 — явний виконавець (on-behalf: сервер дефолтить менеджера). */
  assignedUserId?: string;
  // task / follow_up
  title?: string;
  description?: string;
  dueDate?: string;
  // internal_request
  requestType?: string;
  subject?: string;
  body?: string;
  departmentId?: string;
  formData?: Record<string, string>;
  // suggestion
  fieldCode?: string;
  suggestedValue?: unknown;
  suggestedDisplay?: string;
  message?: string;
}

export interface AcceptedActionResult {
  suggestionKey: string;
  kind: AcceptedAction["kind"];
  status: "created" | "exists";
  entityId: string;
  number?: string | null;
}

export interface SubmitReportPayload {
  occurred?: "yes" | "no_client_cancelled" | "no_manager_unable" | "cancelled";
  text: string;
  /** C+ (D-054): required server-side при occurred=yes. */
  outcome?: ReportOutcome;
  riskMarkers?: ReportRiskMarker[];
  nextActionRequired?: "yes" | "no" | "unknown";
  /** Explicit «без дій + причина» (required server-side, якщо дій немає). */
  nextActionReason?: string;
  /** @deprecated S2 inline rows — лишається для сумісності; S3 = acceptedActions. */
  actions: ReportActionInput[];
  /** D-055 S3 — staged-дії (canonical engines, atomic server-side). */
  acceptedActions?: AcceptedAction[];
  /**
   * TASK-ATT v1.3 (W3/W5) — C+ FULL merged list of bound attachment ids
   * (existing kept + new). Link-multiple REPLACES the set; omitting an id
   * UNLINKS it. Staged in saveReportDraft, atomic-bound on submit.
   */
  attachmentsIds?: string[];
  /** D-151 S2 (P73) — CAS-токен, ОБОВ'ЯЗКОВИЙ на всіх write-шляхах. */
  interactionRevision?: number;
}

export interface SubmitReportResponse {
  status: "success";
  interactionId: string;
  meetingStatus: string | null;
  warnings: string[];
  tasksCreated: { actionItemId: string | null; taskId: string | null }[];
  /** D-055 S3 — результат по кожній staged-дії. */
  actions?: AcceptedActionResult[];
}

export interface MeetingListItem {
  id: string;
  summary: string;
  goal: string | null;
  occurredAt: string | null;
  durationMinutes: number | null;
  contactName: string | null;
  meetingType: string | null;
  meetingStatus: string;
  reportSubmitted: boolean;
  reportRequired: boolean;
  /** S4 — SLA-дані для ReportRequiredBanner (екран 2). */
  reportSlaDueAt: string | null;
  reportOverdueSince: string | null;
  allowedActions: { canFillReport: boolean };
}

export interface MeetingListResponse {
  items: MeetingListItem[];
  total: number;
}

export function scheduleMeeting(
  holdingId: string,
  body: ScheduleMeetingPayload,
): Promise<ScheduleMeetingResponse> {
  return apiFetch<ScheduleMeetingResponse>(
    `/Custom/frontend/client360/${encodeURIComponent(holdingId)}/meetings`,
    { method: "POST", body },
  );
}

export function submitMeetingReport(
  interactionId: string,
  body: SubmitReportPayload,
): Promise<SubmitReportResponse> {
  return apiFetch<SubmitReportResponse>(
    `/Custom/frontend/client360/meetings/${encodeURIComponent(interactionId)}/report`,
    { method: "POST", body },
  );
}

export function fetchMeetings(
  holdingId: string,
  signal?: AbortSignal,
): Promise<MeetingListResponse> {
  return apiFetch<MeetingListResponse>(
    `/Custom/frontend/client360/${encodeURIComponent(holdingId)}/meetings`,
    { signal },
  );
}

// ── D-055 S2 — report draft (autosave, optimistic-lock) ─────────────────────

export interface ReportDraft {
  interactionId: string;
  text: string;
  outcome: ReportOutcome | null;
  riskMarkers: ReportRiskMarker[];
  nextActionReason: string;
  reportSubmitted: boolean;
  reviewStatus: "not_required" | "pending_review" | "accepted" | "returned";
  reviewComment: string;
  /**
   * TASK-ATT v1.3 (W3) — saved (persisted) attachments already bound to this
   * CCI report, so they survive autosave/lock/reload. Feed into
   * AttachmentUpload.saved. Optional: older backends may omit it.
   */
  attachments?: Attachment[];
  /** Optimistic-lock base — повертати у saveReportDraft.baseModifiedAt. */
  modifiedAt: string;
  /** D-151 S2 (P73) — CAS-токен (заміна modifiedAt-токена, який заборонено). */
  interactionRevision?: number;
  reportDraftSavedAt?: string | null;
  limits: {
    minLengthNormal: number;
    minLengthRisk: number;
    riskOutcomes: string[];
  };
}

export interface SaveReportDraftPayload {
  text?: string;
  outcome?: ReportOutcome | null;
  riskMarkers?: ReportRiskMarker[];
  nextActionReason?: string;
  baseModifiedAt?: string;
  /** TASK-ATT v1.3 (W3) — stage bound attachment ids in the server-draft. */
  attachmentsIds?: string[];
  /** D-151 S2 (P73) — CAS-токен, обов'язковий. */
  interactionRevision: number;
}

export interface SaveReportDraftResponse {
  status: "draft_saved";
  interactionId: string;
  modifiedAt: string;
  interactionRevision: number;
}

export function getReportDraft(
  interactionId: string,
  signal?: AbortSignal,
): Promise<ReportDraft> {
  return apiFetch<ReportDraft>(
    `/Custom/frontend/client360/meetings/${encodeURIComponent(interactionId)}/report-draft`,
    { signal },
  );
}

export function saveReportDraft(
  interactionId: string,
  body: SaveReportDraftPayload,
): Promise<SaveReportDraftResponse> {
  return apiFetch<SaveReportDraftResponse>(
    `/Custom/frontend/client360/meetings/${encodeURIComponent(interactionId)}/report-draft`,
    { method: "POST", body },
  );
}


// ── D-151 S2 — on-behalf submit (Q4-A) + tmp-транспорт файлів (P67) ─────────

export interface OnBehalfSubmitPayload {
  reason: string;
  confirmOccurredOnBehalf?: boolean;
  overrideStaleDraft?: boolean;
  expectedAssignedUserId: string;
  interactionRevision: number;
  clientRequestId: string;
  requestHash: string;
  text: string;
  outcome?: ReportOutcome;
  riskMarkers?: ReportRiskMarker[];
  nextActionReason?: string;
  acceptedActions?: AcceptedAction[];
  /** tmp-файли (write-free до submit; у TX стануть Attachment + bind). */
  tmpFileIds?: string[];
}

export interface OnBehalfSubmitResponse {
  status: "success";
  interactionId: string;
  meetingStatus: string | null;
  mode: "supervisor_on_behalf";
  submittedForUserId: string;
  warnings: string[];
  actions?: AcceptedActionResult[];
  attachmentIds?: string[];
  snapshotId?: string | null;
  occurredConfirmed?: boolean;
  idempotent?: boolean;
}

export function submitMeetingReportOnBehalf(
  interactionId: string,
  body: OnBehalfSubmitPayload,
): Promise<OnBehalfSubmitResponse> {
  return apiFetch<OnBehalfSubmitResponse>(
    `/Custom/frontend/meetings/${encodeURIComponent(interactionId)}/report-on-behalf`,
    { method: "POST", body },
  );
}

export interface TmpFileResult {
  tmpId: string;
  name: string;
  size: number;
}

export function uploadOnBehalfTmpFile(body: {
  clientRequestId: string;
  name: string;
  type: string;
  dataBase64: string;
}): Promise<TmpFileResult> {
  return apiFetch<TmpFileResult>("/Custom/frontend/report-onbehalf/tmp-files", {
    method: "POST",
    body,
  });
}

export function removeOnBehalfTmpFile(body: {
  clientRequestId: string;
  tmpId: string;
}): Promise<{ status: string }> {
  return apiFetch("/Custom/frontend/report-onbehalf/tmp-files/remove", {
    method: "POST",
    body,
  });
}
